import { Component, OnInit, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RoomsService } from '../../../../core/services/rooms.service';
import { Room } from '../../../../core/models/room.model';
import { RoomFormModal } from '../room-form-modal/room-form-modal';
import { Store } from '@ngrx/store';
import { selectUserInfo } from '../../../../core/store/auth/auth.selectors';
import { take, filter, map } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import {
  AppPrimeTable,
  PrimeTableColumn,
} from '../../../../shared/components/prime-table/prime-table';
import { AppButton } from '../../../../shared/components/app-button/app-button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MenuItem, ConfirmationService } from 'primeng/api';
import { AppDropdown } from '../../../../shared/components/dropdown/dropdown';
import { ButtonModule } from 'primeng/button';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { toast } from 'ngx-sonner';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import { PermissionService, MODULE } from '../../../../core/services/permission.service';

@Component({
  selector: 'app-list-of-rooms',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RoomFormModal,
    AppPrimeTable,
    AppButton,
    TranslateModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    ToggleSwitchModule,
    ConfirmDialogModule,
    AppDropdown,
    HasPermissionDirective,
  ],
  providers: [ConfirmationService],
  templateUrl: './list-of-rooms.html',
})
export class ListOfRooms implements OnInit {
  rooms: Room[] = [];
  loading = false;
  isFetching = false;

  private confirmationService = inject(ConfirmationService);

  cols: PrimeTableColumn[] = [
    { field: 'name', header: 'rooms_field_name', sortable: true },
    { field: 'capacity', header: 'rooms_field_capacity', sortable: true },
    { field: 'location', header: 'rooms_field_location', sortable: true },
    { field: 'status', header: 'rooms_field_status', type: 'custom', sortable: false },
    { field: 'actions', header: 'table_action', type: 'action', width: '100px' },
  ];

  // Pagination
  currentPage = 1;
  pageSize = 10;
  totalRecords = 0;
  searchText = '';
  showInactive = false;

  // Modals and Menus
  editingRoom: Room | null = null;
  showNewRoom = false;
  menuItems: MenuItem[] = [];
  selectedRoom: Room | null = null;

  private store = inject(Store);
  private userInfo$ = this.store.select(selectUserInfo);
  private translate = inject(TranslateService);
  private perm = inject(PermissionService);

  constructor(
    private roomsService: RoomsService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    // We removed loadRooms() from here because p-table [lazy]="true"
    // will automatically trigger onLazyLoad on initialization.
  }

  async loadRooms() {
    if (this.isFetching) return;

    this.isFetching = true;
    this.loading = true;
    this.cdr.detectChanges();

    try {
      const userInfo = await firstValueFrom(
        this.userInfo$.pipe(
          filter((u) => !!u),
          take(1),
        ),
      );

      const branchId = userInfo?.branches_id || userInfo?.branch_id;

      if (!branchId) {
        console.error('No branch ID found for user');
        this.loading = false;
        this.cdr.detectChanges();
        return;
      }

      const { data, total_records, error } = await this.roomsService.getRooms(
        this.currentPage,
        this.searchText,
        branchId,
        !this.showInactive,
        this.pageSize,
      );

      if (data) {
        this.rooms = data;
        this.totalRecords = total_records || 0;
      } else {
        console.error('Error fetching rooms', error);
        this.rooms = [];
        this.totalRecords = 0;
      }
    } catch (e) {
      console.error('Exception fetching rooms', e);
    } finally {
      this.loading = false;
      this.isFetching = false;
      this.cdr.detectChanges();
    }
  }

  onLazyLoad(event: any) {
    this.currentPage = Math.floor(event.first / event.rows) + 1;
    this.pageSize = event.rows;
    this.loadRooms();
  }

  async onSearch(text: string) {
    this.searchText = text;
    this.currentPage = 1;
    await this.loadRooms();
  }

  openEdit(room: Room) {
    this.editingRoom = room;
  }

  async toggleStatus(room: Room) {
    if (!this.perm.canEdit(MODULE.SALAS)) return; // status change requires edit access
    const isActive = !!room.active;

    if (isActive) {
      this.confirmationService.confirm({
        message: this.translate.instant('CONFIRM_DEACTIVATE_ROOM'),
        header: this.translate.instant('CONFIRMATION'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.performToggle(room, false);
        },
      });
    } else {
      this.performToggle(room, true);
    }
  }

  private async performToggle(room: Room, newStatus: boolean) {
    try {
      const { error } = await this.roomsService.updateRoom(room.id, { active: newStatus });
      if (error) throw error;

      room.active = newStatus;
      toast.success(this.translate.instant('rooms_save_success'));

      // Forces the menu to close if it was open (since row might disappear)
      this.loadRooms();
    } catch (err) {
      console.error('Error toggling status:', err);
      toast.error(this.translate.instant('rooms_save_error'));
    }
  }

  openNewRoom() {
    this.editingRoom = null;
    this.showNewRoom = true;
  }

  onNewRoomSuccess() {
    this.showNewRoom = false;
    this.editingRoom = null;
    this.loadRooms();
  }
}
